import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CooldownTypes = 'quotation.mail'; // Add more types here

const cooldownDurations: Record<CooldownTypes, number> = {
  'quotation.mail': 60_000,
};

type CooldownMap = Partial<Record<CooldownTypes, number>>;

type CooldownStore = {
  cooldowns: CooldownMap;
  setCooldown: (key: CooldownTypes) => void;
  getDuration: (key: CooldownTypes) => number;
};

export const useCooldownStore = create<CooldownStore>()(
  persist(
    (set) => ({
      cooldowns: {},
      setCooldown: (key) => {
        set((state) => ({
          cooldowns: {
            ...state.cooldowns,
            [key]: Date.now(),
          },
        }));
      },
      getDuration: (key) => cooldownDurations[key],
    }),
    { name: 'cooldown-store' },
  ),
);

export function useCooldown(key: CooldownTypes) {
  const timestamp = useCooldownStore((s) => s.cooldowns[key]);
  const durationMs = useCooldownStore((s) => s.getDuration(key));

  const [remainingMs, setRemainingMs] = useState(0);
  const isCoolingDown = remainingMs > 0;

  useEffect(() => {
    if (!timestamp || !durationMs) {
      setRemainingMs(0);
      return;
    }

    const update = () => {
      const now = Date.now();
      const elapsed = now - timestamp;
      const timeLeft = durationMs - elapsed;
      setRemainingMs(Math.max(0, timeLeft));
    };

    update();
    const interval = setInterval(update, 250);

    return () => clearInterval(interval);
  }, [timestamp, durationMs]);

  return {
    isCoolingDown,
    remainingMs,
    remainingSeconds: Math.ceil(remainingMs / 1000),
  };
}
